package com.dynamicruntime.webapp

import com.dynamicruntime.common.endpoint.EI
import com.dynamicruntime.common.gedra.SearchRole
import com.dynamicruntime.common.gedra.UsageKind
import com.dynamicruntime.common.gedra.choiceLabel
import com.dynamicruntime.common.gedra.decodeSearchParam
import com.dynamicruntime.common.gedra.reservedQueryFieldNames
import com.dynamicruntime.common.gedra.yesNoText
import com.dynamicruntime.common.gedra.workflow.SVY
import com.dynamicruntime.common.schema.SCH
import com.dynamicruntime.common.schema.SchOption
import com.dynamicruntime.common.util.toJsonMapOrEmpty
import react.FC
import react.Props
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useEffect
import react.useRef
import react.useState
import web.cssom.ClassName

/**
 * The chip for an applied survey-status filter (issue #695), `Status: Valid`, or null when none is applied --
 * beside the trait chips [activeFilterChips] makes, so a narrowed list never looks like the whole. Pure, and
 * covered under `jsNodeTest`.
 */
fun surveyStatusChip(applied: Map<String, Any?>): String? =
    SurveyStatus.fromWire(applied[SVY.surveyStatus]?.toString())?.let { "Status: ${it.label}" }

/** How long a type-ahead waits after a keystroke before it fetches, so a fast typist makes one call not many. */
private const val suggestDebounceMs = 200

/** The filter well's element id, so the toggle can name what it controls (`aria-controls`). */
private const val formsFiltersWellId = "forms-filters"

/** At most this many user suggestions in the scope bar's dropdown -- a short list to pick from, not a listing. */
const val maxUserSuggestions = 8

/** How many value suggestions a filter box asks for -- a short dropdown, capped again on the backend. */
const val maxValueSuggestions = 10

/**
 * One user the scope-bar type-ahead offers (issue #581): the [label] shown (a name or public name with the
 * email, or the email alone) and the [value] a pick sends -- the email, which the listing's `user` parameter
 * resolves within the caller's own access.
 */
class UserPick(val label: String, val value: String)

/** The browser's `setTimeout`/`clearTimeout`, declared locally rather than reaching for a DOM wrapper -- the
 *  same idiom the app bar and the users page use for their debounces. */
private fun setTimer(block: () -> Unit, delayMs: Int): Int = js("setTimeout(block, delayMs)") as Int
private fun clearTimer(id: Int) {
    js("clearTimeout(id)")
}

/** antd `{label, value}` options for the user suggestions. */
private fun userPickOptions(picks: List<UserPick>): Array<dynamic> = picks.map { pick ->
    val o: dynamic = js("({})")
    o.label = pick.label
    o.value = pick.value
    o
}.toTypedArray()

/** antd `{label, value}` options for a yes-or-no filter (issue #1508): Yes and No, as the column's cells read. */
private fun yesNoOptions(): Array<dynamic> = listOf("true" to "Yes", "false" to "No").map { (value, label) ->
    val o: dynamic = js("({})")
    o.label = label
    o.value = value
    o
}.toTypedArray()

/** antd `{label, value}` options for the survey-status filter (issue #695): the three statuses, labelled as the chip labels them. */
private fun surveyStatusOptions(): Array<dynamic> = SurveyStatus.entries.map { s ->
    val o: dynamic = js("({})")
    o.label = s.label
    o.value = s.wire
    o
}.toTypedArray()

/**
 * What a text filter box suggests for the typed [term] (issue #1500): a column's [choices] first, by label, in their
 * declared order -- every one for a blank term, so a focused box offers them before anything is typed -- then the
 * values [fetched] from the data that are not among them, so a value from before the client narrowed its choices can
 * still be found. A choice matches the term by its label or its value; a fetched value that is a choice is shown by its
 * label, once. The box takes the label, which the column shows and its search matches. Without choices, the fetched
 * values as they are. Pure, and covered under `jsNodeTest`.
 */
fun valueSuggestions(term: String, choices: List<SchOption>, fetched: List<String>): List<String> {
    val t = term.trim()
    val offered = choices.filter { t.isEmpty() || it.label.contains(t, ignoreCase = true) || it.value.contains(t, ignoreCase = true) }
        .map { it.label }
    return (offered + fetched.map { choiceLabel(it, choices) ?: it }).distinct()
}

/** antd `{value}` options for value suggestions, where the shown label is the value itself. */
private fun stringOptions(values: List<String>): Array<dynamic> = values.map { v ->
    val o: dynamic = js("({})")
    o.value = v
    o
}.toTypedArray()

/**
 * One trait's search controls, grouped (issue #562): the parameters the listing declares for a single trait --
 * an exact and maybe a substring match for text, a lower and upper bound for a number or date -- folded into
 * the one control a person expects: a text box, or a from-to pair. Derived from the listing endpoint's own
 * input schema (issue #538), so it is exactly what the client's variant advertises -- a control appears only
 * when the backend will accept its parameter. The parameter *names* are what the box sends; the [label] is
 * the column's, with the role's own words removed, falling back to the column's name ([columnKey], issue #1490).
 */
class SearchGroup(
    val columnKey: String,
    val label: String,
    val kind: UsageKind,
    /** The exact-match parameter, when declared. */
    val exact: String?,
    /** The substring parameter, when the usage asked for one. */
    val contains: String?,
    /** The lower bound, for a number or date. */
    val min: String?,
    /** The upper bound, for a number or date. */
    val max: String?,
) {
    /**
     * The parameter a text box sends: the substring one when it is declared, since "contains" is what a
     * person expects a search box to do, else the exact one. Null for a range.
     */
    val text: String? get() = contains ?: exact

    /** Whether the trait is searched as a from-to range rather than a text box. */
    val isRange: Boolean get() = min != null || max != null
}

/**
 * The search groups a listing endpoint's [inputSchema] declares (issue #562): every input property that is not
 * a reserved paging/scope field, gathered by trait into one [SearchGroup] each, in the order the traits first
 * appear. A client with no usage rules has none, so the caller shows no search at all. The grouping reads the
 * parameter names back through the kernel's own decoder ([decodeSearchParam]), so it is the generator's naming
 * inverted rather than a second spelling of it. Pure, and covered under `jsNodeTest`.
 */
fun searchGroups(inputSchema: Map<String, Any?>): List<SearchGroup> {
    class Draft(val columnKey: String, val kind: UsageKind) {
        var label: String? = null
        var exact: String? = null
        var contains: String? = null
        var min: String? = null
        var max: String? = null
    }

    val properties = inputSchema[SCH.properties].toJsonMapOrEmpty()
    val drafts = LinkedHashMap<String, Draft>()
    for ((name, body) in properties) {
        // The listing's own fields (the kernel's list, shared with the backend's check): never a trait search box.
        if (name in reservedQueryFieldNames) continue
        val prop = body.toJsonMapOrEmpty()
        val shape = decodeSearchParam(name, prop, properties.keys)
        val draft = drafts.getOrPut(shape.columnKey) { Draft(shape.columnKey, shape.kind) }
        // The caption is the column label: the parameter's title with the role's own words removed.
        if (draft.label == null) draft.label = (prop[SCH.title] as? String)?.removeSuffix(shape.role.labelSuffix)
        when (shape.role) {
            SearchRole.exact -> draft.exact = name
            SearchRole.contains -> draft.contains = name
            SearchRole.min -> draft.min = name
            SearchRole.max -> draft.max = name
        }
    }
    return drafts.values.map { SearchGroup(it.columnKey, it.label ?: it.columnKey, it.kind, it.exact, it.contains, it.min, it.max) }
}

/**
 * The filters in [applied] said in words, one per trait with a value, for the summary the collapsed panel
 * shows (issue #562): `Name contains "plan"`, `Year 2020 – 2025`, `Due ≤ 2026-01-01`. Only the groups'
 * own parameters count -- the free-text term and the user scope have their own controls -- and a blank value
 * is no filter. Pure, and covered under `jsNodeTest`.
 */
fun activeFilterChips(groups: List<SearchGroup>, applied: Map<String, Any?>): List<String> = groups.mapNotNull { g ->
    fun valueOf(name: String?): String? = name?.let { applied[it]?.toString() }
    // The words -- and the rule that a blank value is no chip -- are the shared vocabulary in FilterPanel.kt,
    // so this and the users console's chips read the same. The substring parameter wins when both are filled,
    // as the one box that sends it does.
    if (g.isRange) {
        rangeChip(g.label, valueOf(g.min), valueOf(g.max))
    } else if (g.kind == UsageKind.boolean) {
        // A yes-or-no reads as the cells do (issue #1508).
        valueOf(g.exact)?.let { yesNoText(it) }?.let { "${g.label}: $it" }
    } else {
        textChip(g.label, valueOf(g.contains), contains = true) ?: textChip(g.label, valueOf(g.exact), contains = false)
    }
}

/**
 * The scope bar (issue #562, a type-ahead in #581), for a caller who administers other users: whose forms the
 * list shows. Promoted above the search rather than filed among the filters, because it changes *whose*
 * documents are on screen where a filter narrows *which* -- and drawn in the elevated-privilege tint so it
 * reads as the administrative control it is.
 *
 * A type-ahead over the users the caller administers: typing a name, username, or email fetches matching users
 * (debounced) and picking one confines the list to them. A raw id or email typed and applied without picking
 * still resolves through the listing's `user` parameter, which confines whatever is sent to the caller's own
 * access -- the suggestions only make the box easier to fill, never wider.
 */
external interface FormsScopeBarProps : Props {
    /** What the box holds. */
    var value: String

    /** The user the list is currently confined to, or null for everyone the caller may see. */
    var applied: String?

    /**
     * Who the list is confined to, as the listing itself said (issues #1081, #1095): which user [applied] was
     * resolved to. For an id it puts a name to a number; for an address it says which of that person's users it
     * meant. Null when no user is applied, or the listing has not answered.
     */
    var appliedWho: String?

    /** Records a keystroke in the box. */
    var onChange: (String) -> Unit

    /** Confines the list to the user in the box (the raw-entry path: a typed id or email). */
    var onApply: () -> Unit

    /** Drops the user and shows everyone again. */
    var onShowEveryone: () -> Unit

    /**
     * Fetches user suggestions for a typed `term`, calling back with them (issue #581). Owned by the parent so
     * this stays free of the API; the box debounces the calls. A failure calls back with an empty list, which
     * leaves the box working as plain text.
     */
    var fetchUsers: (String, (List<UserPick>) -> Unit) -> Unit

    /** Confines the list to the picked user (by email), keeping the applied filters -- the pick path, distinct
     *  from [onApply]'s raw-entry path, so a stale draft never rides along. */
    var onPick: (String) -> Unit
}

val FormsScopeBar = FC<FormsScopeBarProps> { props ->
    var suggestions by useState<List<UserPick>>(emptyList())
    val timer = useRef<Int>(null)

    // Debounce a suggestion fetch on the typed value. A short term is not worth a search, so it clears the list
    // (and a name/email fragment is meaningful only from a couple of characters). Cleanup on the next run, the
    // codebase's idiom, so a fast typist never leaves two fetches racing.
    useEffect(props.value) {
        timer.current?.let { clearTimer(it) }
        timer.current = null
        val term = props.value.trim()
        if (term.length < 2) {
            suggestions = emptyList()
        } else {
            timer.current = setTimer({ props.fetchUsers(term) { suggestions = it } }, suggestDebounceMs)
        }
    }

    div {
        className = ClassName("row forms-scope")
        span {
            className = ClassName("forms-scope-label")
            +"Showing forms for"
        }
        AutoComplete {
            placeholder = "everyone whose forms you see — or a name, email, or id"
            value = props.value
            options = userPickOptions(suggestions)
            allowClear = true
            // Show every fetched suggestion; the backend already narrowed them to the term.
            filterOption = false
            // Enter submits what was typed (a raw id or email), never a suggestion that merely matched a
            // fragment -- a suggestion is taken by clicking it or arrowing to it.
            defaultActiveFirstOption = false
            style = js("({ width: 360 })")
            onChange = { v -> props.onChange((v as? String) ?: "") }
            onSelect = { v ->
                suggestions = emptyList()
                props.onPick((v as? String) ?: "")
            }
            onInputKeyDown = { event -> if (event.key == "Enter") props.onApply() }
        }
        Button {
            onClick = { props.onApply() }
            +"Apply"
        }
        if (props.applied != null) {
            Button {
                type = "link"
                onClick = { props.onShowEveryone() }
                +"Show everyone"
            }
        }
        // Who the list is confined to, on a line of its own under the controls, so they never move to make room
        // for a name. Shown while the box still holds what is applied: once something else is typed, the name is
        // of the list on screen and no longer of the box.
        val who = props.appliedWho
        if (who != null && props.value.trim() == props.applied?.trim()) {
            span {
                className = ClassName("forms-scope-who")
                +who
            }
        }
    }
}

/**
 * The forms-list search (issue #538, regrouped in #562, type-ahead in #581): one box that searches every text
 * field at once, and behind a toggle the per-trait filters the client's usage rules declared -- one text box
 * per text trait, a from-to pair per number or date, a Yes/No choice per boolean (issue #1508) -- so the list keeps
 * the screen and the filters are there when wanted. While the panel is closed, the applied filters are summarized as
 * chips so a narrowed list never looks like the whole -- the toggle, the chips and the well are the shell
 * `FilterPanel.kt` shares with the users console (issue #683). A text trait's box suggests the distinct values that
 * trait takes, when the caller's surface can supply them ([fetchValues]).
 *
 * The parent owns the values and every action; the widgets own only their own suggestion state and debounce.
 */
external interface FormsSearchProps : Props {
    /** The declared search groups, in display order. */
    var groups: List<SearchGroup>

    /** The current value of each parameter, keyed by name; the free-text term is under [EI.q]. */
    var values: Map<String, String>

    /** The parameters the list is currently filtered by, for the chips and the count. */
    var applied: Map<String, Any?>

    /** Whether the filter panel is open. */
    var panelOpen: Boolean

    /** Opens or closes the filter panel. */
    var onTogglePanel: () -> Unit

    /** Records a keystroke in one parameter's box. */
    var onChange: (String, String) -> Unit

    /** Applies the current values as the list's filter. */
    var onSearch: () -> Unit

    /** Clears the term and every filter (not the user scope, which has its own control). */
    var onClear: () -> Unit

    /**
     * Whether to offer the survey-status filter (issue #695): true when the caller's client declares a survey
     * (the shell's `hasSurvey`), which is a fact about the client rather than about the rows on screen -- a
     * control that vanished the moment a filter matched nothing could never be cleared. Unset means no.
     */
    var showSurveyStatus: Boolean?

    /**
     * Fetches distinct value suggestions for a text trait (issue #581): given the trait id and a typed prefix,
     * calls back with the values. Null when the caller's surface carries no values endpoint (an older node),
     * and then the text boxes stay plain inputs. A failure calls back empty, leaving the box usable as text.
     */
    var fetchValues: ((String, String, (List<String>) -> Unit) -> Unit)?

    /**
     * The choices each column's field offers (issue #1500), by column ([SearchGroup.columnKey]), from the listing's
     * columns: what a column's box suggests ahead of the values in the data ([valueSuggestions]). Unset or absent for
     * a column means none.
     */
    var choices: Map<String, List<SchOption>>?
}

val FormsSearch = FC<FormsSearchProps> { props ->
    val groups = props.groups
    // The free-text term searches the text fields, so the box is offered only where there is one to search.
    val hasText = groups.any { it.kind == UsageKind.string }
    val chips = activeFilterChips(groups, props.applied) + listOfNotNull(surveyStatusChip(props.applied))
    val termApplied = props.applied[EI.q]?.toString()?.isNotBlank() == true
    div {
        className = ClassName("row forms-toolbar")
        if (hasText) {
            Input {
                placeholder = "Search all text fields"
                value = props.values[EI.q] ?: ""
                allowClear = true
                style = js("({ width: 300 })")
                onChange = { event -> props.onChange(EI.q, event.target.value as? String ?: "") }
                onPressEnter = { props.onSearch() }
            }
            Button {
                type = "primary"
                onClick = { props.onSearch() }
                +"Search"
            }
        }
        filterToggle(props.panelOpen, chips.size, formsFiltersWellId) { props.onTogglePanel() }
        if (termApplied || chips.isNotEmpty()) {
            Button {
                type = "link"
                onClick = { props.onClear() }
                +"Clear"
            }
        }
    }
    filterChips(chips, props.panelOpen)
    filterWell(props.panelOpen, formsFiltersWellId) {
        // The survey-status filter (issue #695): a closed choice over the three statuses the column shows, first
        // in the well since it is the one filter every form has rather than a trait's. Cleared means any.
        if (props.showSurveyStatus == true) {
            filterGroup("Status") {
                Select {
                    value = props.values[SVY.surveyStatus]?.ifBlank { null }
                    options = surveyStatusOptions()
                    allowClear = true
                    placeholder = "Any"
                    style = js("({ minWidth: 160 })")
                    onChange = { v -> props.onChange(SVY.surveyStatus, (v as? String) ?: "") }
                }
            }
        }
        groups.forEach { group ->
            filterGroup(group.label) {
                val yesNo = group.exact?.takeIf { group.kind == UsageKind.boolean }
                if (yesNo != null) {
                    // A yes-or-no (issue #1508): a closed choice, as a boolean has only two answers. Cleared means any.
                    Select {
                        value = props.values[yesNo]?.ifBlank { null }
                        options = yesNoOptions()
                        allowClear = true
                        placeholder = "Any"
                        style = js("({ minWidth: 120 })")
                        onChange = { v -> props.onChange(yesNo, (v as? String) ?: "") }
                    }
                } else if (group.isRange) {
                    // A date's ends are day pickers (issue #1500), which still take a typed day.
                    fun react.ChildrenBuilder.end(name: String?, placeholder: String) =
                        if (group.kind == UsageKind.date) dayBoundInput(props, name, placeholder) else boundInput(props, name, placeholder)
                    div {
                        className = ClassName("filter-range")
                        end(group.min, "from")
                        span {
                            className = ClassName("type-hint")
                            +"to"
                        }
                        end(group.max, "to")
                    }
                } else {
                    group.text?.let { name ->
                        val placeholder = if (group.contains != null) "contains" else "is exactly"
                        val fetch = props.fetchValues
                        val choices = props.choices?.get(group.columnKey).orEmpty()
                        if (fetch != null || choices.isNotEmpty()) {
                            FilterValueBox {
                                this.columnKey = group.columnKey
                                this.placeholder = placeholder
                                this.value = props.values[name] ?: ""
                                this.onChange = { v -> props.onChange(name, v) }
                                this.onSearch = { props.onSearch() }
                                this.fetchValues = fetch
                                this.choices = choices
                            }
                        } else {
                            Input {
                                this.placeholder = placeholder
                                value = props.values[name] ?: ""
                                allowClear = true
                                style = js("({ width: 260 })")
                                onChange = { event -> props.onChange(name, event.target.value as? String ?: "") }
                                onPressEnter = { props.onSearch() }
                            }
                        }
                    }
                }
            }
        }
        div {
            className = ClassName("row filter-actions")
            Button {
                type = "primary"
                onClick = { props.onSearch() }
                +"Apply filters"
            }
            Button {
                type = "link"
                onClick = { props.onClear() }
                +"Clear filters"
            }
        }
    }
}

/**
 * One text-column filter box with a value type-ahead (issue #581): as the person types, it suggests the distinct
 * values that column takes across the caller's documents. Its own component so each box holds its own suggestion
 * state and debounce. The value it holds is still a plain "contains" fragment -- picking a suggestion only
 * fills the box, and the panel's Apply is what searches -- so an empty or failed fetch leaves it a text input.
 *
 * A column whose field offers [choices] (issue #1500) suggests them too, ahead of the fetched values and before
 * anything is typed ([valueSuggestions]). The list stays open: anything typed or pasted is searched for as it is.
 */
private external interface FilterValueBoxProps : Props {
    var columnKey: String
    var placeholder: String
    var value: String
    var onChange: (String) -> Unit
    var onSearch: () -> Unit
    var fetchValues: ((String, String, (List<String>) -> Unit) -> Unit)?
    var choices: List<SchOption>
}

private val FilterValueBox = FC<FilterValueBoxProps> { props ->
    var fetched by useState<List<String>>(emptyList())
    val timer = useRef<Int>(null)

    useEffect(props.value) {
        timer.current?.let { clearTimer(it) }
        timer.current = null
        val term = props.value.trim()
        val fetch = props.fetchValues
        if (term.isEmpty() || fetch == null) {
            fetched = emptyList()
        } else {
            timer.current = setTimer({ fetch(props.columnKey, term) { fetched = it } }, suggestDebounceMs)
        }
    }

    AutoComplete {
        placeholder = props.placeholder
        value = props.value
        options = stringOptions(valueSuggestions(props.value, props.choices, fetched))
        allowClear = true
        filterOption = false
        // Picking a suggestion only fills the box; Enter runs the search, as the plain input it replaces did.
        defaultActiveFirstOption = false
        style = js("({ width: 260 })")
        onChange = { v -> props.onChange((v as? String) ?: "") }
        onInputKeyDown = { event -> if (event.key == "Enter") props.onSearch() }
    }
}

/**
 * One end of a date's from-to pair (issue #1500): a day picker for the bound parameter [name], or nothing when the pair
 * lacks it. It holds the day as the parameter takes it, `yyyy-mm-dd`, and a cleared picker is no bound.
 *
 * A bound that does not read as a date -- from an old link, say -- is shown in a text box instead, as it was before
 * the picker: a picker would draw it empty while it is still sent, a filter nobody can see or clear.
 */
private fun react.ChildrenBuilder.dayBoundInput(props: FormsSearchProps, name: String?, placeholder: String) {
    if (name == null) return
    val day = props.values[name]?.trim().orEmpty()
    val picked = day.ifEmpty { null }?.let { dayjs(it) }
    if (picked != null && !picked.isValid()) {
        boundInput(props, name, placeholder)
        return
    }
    DatePicker {
        this.placeholder = placeholder
        value = picked
        style = js("({ width: 150 })")
        onChange = { date, _ -> props.onChange(name, date?.format(dayPattern) ?: "") }
    }
}

/** How a day picker writes its day for a date bound: the `yyyy-mm-dd` a date search parameter reads. */
private const val dayPattern = "YYYY-MM-DD"

/** One end of a from-to pair: a text box for the bound parameter [name], or nothing when the pair lacks it. */
private fun react.ChildrenBuilder.boundInput(props: FormsSearchProps, name: String?, placeholder: String) {
    if (name == null) return
    Input {
        this.placeholder = placeholder
        value = props.values[name] ?: ""
        allowClear = true
        style = js("({ width: 150 })")
        onChange = { event -> props.onChange(name, event.target.value as? String ?: "") }
        onPressEnter = { props.onSearch() }
    }
}
